package com.nonopn.skydome.common.app.pages.pdf

import com.nonopn.skydome.common.app.utils.fmt
import kotlin.math.abs

/** Immutable, per-render copy for the Keraunic Report. */
internal data class KeraunicReportText(
    val lang: PdfOutputLang,
    val reportTitle: String,
    val generatedOn: String,
    val editedOn: String,
    val westCardinal: String,
    val shortMonths: List<String>,
    val longMonths: List<String>,
    val cover: CoverText,
    val overview: OverviewText,
    val map: MapText,
    val zone: ZoneText,
    val effects: EffectsText,
    val charts: ChartsText,
    val recommendations: RecommendationsText,
    val methodology: MethodologyText,
    val highlights: HighlightsText,
) {
    fun month(label: String): String {
        val normalized = label.trim().lowercase().trimEnd('.', ',')
        val index = MONTH_ALIASES.indexOfFirst { normalized in it }
        return if (index in shortMonths.indices) shortMonths[index] else label
    }

    // Typographic space before a colon is French only
    fun labelColon(): String = if (lang == PdfOutputLang.FR) " :" else ":"

    fun date(label: String): String {
        val match = NUMERIC_DATE.matchEntire(label.trim()) ?: return label
        val day = match.groupValues[1].toIntOrNull() ?: return label
        val month = match.groupValues[2].toIntOrNull() ?: return label
        val year = match.groupValues[3]
        if (day !in 1..31 || month !in 1..12) return label
        val monthShort = shortMonths[month - 1]
        val monthLong = longMonths[month - 1]
        return when (lang) {
            PdfOutputLang.FR -> "$day $monthLong $year"
            PdfOutputLang.EN -> "$monthShort $day, $year"
            PdfOutputLang.ES, PdfOutputLang.PT -> "$day de $monthLong de $year"
        }
    }

    fun period(label: String): String {
        val separator = " — "
        val parts = label.split(separator)
        return if (parts.size == 2) "${date(parts[0])}$separator${date(parts[1])}" else label
    }

    fun hour(hour: Int): String = when (lang) {
        PdfOutputLang.EN -> "$hour:00"
        PdfOutputLang.FR -> "${hour}h"
        PdfOutputLang.ES, PdfOutputLang.PT -> "$hour h"
    }

    /** "12 juillet 2026 à 14:32 (UTC+2)": date in the report language, hour and UTC offset of the device. */
    fun dateTime(time: KeraunicLocalTime): String {
        val day = "${time.day.toString().padStart(2, '0')}/${time.month.toString().padStart(2, '0')}/${time.year}"
        val clock = "${time.hour.toString().padStart(2, '0')}:${time.minute.toString().padStart(2, '0')}"
        return "${date(day)} ${highlights.at} $clock (${utcOffset(time.offsetMinutes)})"
    }

    /** Date and time of an impact when known, its day otherwise, "date not available" when the server gave none. */
    fun impactDate(impact: KeraunicTopImpact): String =
        impact.localTime?.let(::dateTime)
            ?: impact.dateLabel.takeIf { it.isNotBlank() }?.let(::date)
            ?: highlights.dateUnavailable

    /** "87 kA CG+ · 0.64 km · <date>" (most intense) or "0.12 km · 23 kA CG− · <date>" (nearest). */
    fun highlightLine(highlight: KeraunicHighlight, distanceFirst: Boolean): String {
        val polarity = when (highlight.isPositive) {
            true -> " CG+"
            false -> " CG\u2212"
            null -> ""
        }
        val intensity = "${highlight.amplitudeKa.fmt(0)} kA$polarity"
        val distance = highlight.distKm?.let { "${it.fmt(2)} km" }
        val time = highlight.time?.let(::dateTime) ?: highlights.dateUnavailable
        val parts = if (distanceFirst) listOfNotNull(distance, intensity, time) else listOfNotNull(intensity, distance, time)
        return parts.joinToString(" \u00b7 ")
    }

    /** Replaces each %s of [template] in order (same rule for the screen and the PDF). */
    fun fill(template: String, vararg values: String): String =
        values.fold(template) { result, value -> result.replaceFirst("%s", value) }

    /** "45.1234° N · 5.1234° E" with the localized west cardinal. */
    fun coordinates(lat: Double, lon: Double): String {
        val latDir = if (lat >= 0) "N" else "S"
        val lonDir = if (lon >= 0) "E" else westCardinal
        return "${abs(lat).fmt(4)}\u00b0 $latDir \u00b7 ${abs(lon).fmt(4)}\u00b0 $lonDir"
    }

    private fun utcOffset(minutes: Int): String {
        if (minutes == 0) return "UTC"
        val sign = if (minutes > 0) "+" else "\u2212"
        val h = abs(minutes) / 60
        val m = abs(minutes) % 60
        return if (m == 0) "UTC$sign$h" else "UTC$sign$h:${m.toString().padStart(2, '0')}"
    }

    /** Copy for the screen: the report texts are written for HTML (entities and line breaks). */
    fun plain(html: String): String = html
        .replace("<br/>", " ")
        .replace("&lt;", "<")
        .replace("&gt;", ">")
        .replace("&nbsp;", "\u00a0")
        .replace("&amp;", "&")
        .replace(Regex(" {2,}"), " ")

    fun fallbackImpactLabel(label: String): String = when (label) {
        "Le plus proche" -> overview.nearestImpact
        "Le plus fort" -> overview.strongestImpact
        "Le plus faible" -> overview.weakestImpact
        else -> label
    }

    companion object {
        private val NUMERIC_DATE = Regex("""^(\d{1,2})/(\d{1,2})/(\d{4})$""")
        private val MONTH_ALIASES = listOf(
            setOf("jan", "janv", "janvier", "january", "ene", "enero"),
            setOf("feb", "fév", "fev", "févr", "fevr", "février", "fevrier", "february", "febrero", "fevereiro"),
            setOf("mar", "mars", "march", "marzo"),
            setOf("avr", "avril", "apr", "april", "abr", "abril"),
            setOf("mai", "may", "mayo"),
            setOf("jun", "juin", "june", "junio"),
            setOf("jul", "juil", "juillet", "july", "julio"),
            setOf("aoû", "aou", "août", "aout", "aug", "august", "ago", "agosto"),
            setOf("sep", "sept", "septembre", "september", "set", "setembro", "septiembre"),
            setOf("oct", "octobre", "october", "out", "outubro", "octubre"),
            setOf("nov", "novembre", "november", "noviembre"),
            setOf("déc", "dec", "décembre", "decembre", "december", "dic", "diciembre", "dez", "dezembro"),
        )

        fun forLanguage(lang: PdfOutputLang): KeraunicReportText = when (lang) {
            PdfOutputLang.FR -> fr
            PdfOutputLang.EN -> en
            PdfOutputLang.ES -> es
            PdfOutputLang.PT -> pt
        }

        private val fr = KeraunicReportText(
            lang = PdfOutputLang.FR,
            reportTitle = "Rapport Kéraunique",
            generatedOn = "Généré le",
            editedOn = "Édité le",
            westCardinal = "O",
            shortMonths = listOf("Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"),
            longMonths = listOf("janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"),
            cover = CoverText(
                subtitle = "Analyse complète de l'activité foudre",
                period = "Période",
                impactsDetected = "Impacts détectés (0–2 km)",
                nsg = "Nsg (impacts/km²/an)",
                maximumIntensity = "Intensité maximale",
                directDanger = "Danger direct (&lt; 1 km)",
                nsgLine = "Nsg = %s impacts/km²/an",
                highActivity = "Activité élevée",
                moderateActivity = "Activité modérée",
                lowActivity = "Activité faible",
                referencePrefix = "— Réf. IEC 62858:2019",
            ),
            overview = OverviewText(
                header = "Vue d'ensemble — Impacts remarquables",
                notableImpacts = "Impacts remarquables",
                topSubtitle = "Impacts remarquables à proximité du site (zone 0–2 km)",
                fallbackSubtitle = "Impact le plus proche, le plus fort et le plus faible du site (zone 0–2 km)",
                missingTopImpacts = "Données top impacts non disponibles",
                analysis = "Analyse kéraunique du site",
                nsgComparison = "Comparaison avec les villes proches (Nsg)",
                yourSite = "Votre site",
                nearestCities = "Comparaison avec les villes proches (Nsg)",
                detectionNetwork = "Réseau de détection GLD360+",
                detectionDescription = "Réseau mondial longue portée, efficacité de détection &gt;95%, précision de localisation &lt;&nbsp;500&nbsp;m. Chaque impact est identifié par un ID unique et horodaté en UTC.",
                nearestImpact = "Le plus proche",
                strongestImpact = "Le plus fort",
                weakestImpact = "Le plus faible",
            ),
            map = MapText(
                header = "Carte des impacts",
                title = "Répartition géographique des impacts",
                description = "Chaque point représente un impact détecté par le réseau GLD360+ sur la période d'étude.",
                positive = "positif",
                negative = "négatif",
                directCount = "impacts &lt;1 km",
                directDanger = "Zone danger direct",
                indirectCount = "impacts 1–2 km",
                indirectDanger = "Zone danger indirect",
            ),
            zone = ZoneText(
                directDanger = "DANGER DIRECT",
                indirectDanger = "DANGER INDIRECT",
                directSubtitle = "Zone de danger direct — 0 à 1 km",
                indirectSubtitle = "Zone de danger indirect — 1 à 2 km",
                directDescription = "Sur cette zone, un coup de foudre peut atteindre directement les structures, équipements et personnes",
                indirectDescription = "Dans cette zone, la foudre ne frappe pas le site mais génère des effets à distance mesurables",
                zoneDirect = "Danger direct",
                zoneIndirect = "Danger indirect",
                recordedImpacts = "impacts enregistrés",
                noImpactsInZone = "Aucun impact enregistré dans cette zone sur la période.",
                polarityDistribution = "Répartition de polarité",
                negativeLightning = "Foudre négative (CG−)",
                positiveLightning = "Foudre positive (CG+)",
                impactCount = "Nombre d'impacts",
                maximumIntensity = "Intensité maximale",
                averageIntensity = "Intensité moyenne",
                median = "Médiane",
                keyPoints = "Points clés — Zone danger direct",
                mostIntenseImpact = "Impact le plus intense",
                strongestPositiveImpact = "Impact CG+ le plus puissant",
                distanceFromSite = "Distance : %s km du site",
                moreDestructive = "2 à 3× plus destructeur que la foudre négative",
            ),
            effects = EffectsText(
                header = "Effets possibles &amp; contexte élargi",
                section = "Effets possibles &amp; contexte élargi",
                directTitle = "Coups directs — zone 0–1 km (danger immédiat)",
                fire = "Incendie",
                fireDescription = "Un impact direct sur une structure peut initier un feu par arc électrique ou inflammation de matériaux combustibles.",
                structuralDamage = "Destruction / dommage structurel",
                structuralDamageDescription = "Les courants de foudre élevés (&gt; 50 kA) génèrent des forces mécaniques et thermiques endommageant durablement les structures.",
                lifeSafety = "Risque vital",
                lifeSafetyDescription = "Tension de pas et tension de contact létales dans un rayon de 15–20 m autour du point d'impact.",
                indirectTitle = "Coups indirects — effets LEMP (0–2 km)",
                lempSurges = "Surtensions LEMP",
                lempDescription = "L'impulsion électromagnétique de foudre (LEMP) induit des surtensions transitoires sur tous les conducteurs : câbles d'alimentation, liaisons de données, armoires électriques.",
                automation = "Automates &amp; SCADA",
                automationDescription = "Les automates industriels et systèmes SCADA sont particulièrement vulnérables aux perturbations électromagnétiques.",
                synthesis = "Synthèse",
                synthesisText = "Avec une intensité maximale de %s kA enregistrée à moins de 2 km, %s coups directs (0–1 km) et %s coups indirects (1–2 km) sur la période, le niveau de risque global est : %s.",
                synthesisRiskNote = "Les risques d'incendie, de dommage structurel et de destruction d'équipements sensibles sont avérés et non négligeables.",
                synthesisLowNote = "Les effets indirects (surtensions LEMP) peuvent néanmoins affecter les équipements sensibles.",
            ),
            charts = ChartsText(
                monthlyActivity = "Activité mensuelle",
                monthlyAndIntensityDistribution = "Activité mensuelle &amp; distribution d'intensité",
                zone = "Zone",
                strikesPerMonth = "impacts/mois",
                monthlyIntensityHeader = "Intensité mensuelle CG−/CG+",
                averageMonthlyIntensity = "Intensité moyenne mensuelle par zone (kA)",
                monthlyIntensityDescription = "Intensité moyenne des impacts CG− et CG+ par mois. Les pics d'intensité révèlent les mois les plus dangereux.",
                averageKaPerMonth = "kA moyen/mois",
                hourlyDistributionHeader = "Distribution horaire de l'activité",
                hourlyDistribution = "Distribution horaire (UTC) — polarité CG−/CG+",
                allImpactsAroundSite = "Tous impacts autour du site",
                combinedZones = "zones 0–1 km et 1–2 km cumulées",
                monthlyHourlyIntensityHeader = "Intensité horaire — CG−/CG+",
                averageHourlyIntensity = "Intensité moyenne par heure UTC (kA)",
                dailyProfile = "Le profil diurne révèle une aggravation de l'intensité qui s'ajoute au pic de fréquence.",
                averageKaPerHour = "kA moyen/heure UTC, zones cumulées",
                negativeSeries = "CG− (négatifs)",
                positiveSeries = "CG+ (positifs)",
                notAvailable = "Données non disponibles",
            ),
            recommendations = RecommendationsText(
                header = "Diagnostic personnalisé &amp; recommandations",
                riskHigh = "RISQUE ÉLEVÉ",
                riskModerate = "RISQUE MODÉRÉ",
                riskLow = "RISQUE FAIBLE",
                gaugeLow = "FAIBLE",
                gaugeCritical = "CRITIQUE",
                scoreDescription = "Score calculé sur la base des %s impacts directs (0–1 km),<br/> de l'intensité maximale observée (%s kA) et du Nsg (%s).",
                actionPlan = "Plan d'action recommandé",
                priority1 = "PRIORITÉ 1",
                priority2 = "PRIORITÉ 2",
                advice = "CONSEIL",
                skySentinel = "SKY SENTINEL",
                installRodTitle = "Installation d'un paratonnerre PDA (NF C 17-102:2011)",
                installRodDescription = "Avec %s impacts directs sur la période et une intensité max de %s kA, l'installation d'un paratonnerre à dispositif d'amorçage est indispensable.",
                groundingTitle = "Mise à la terre et liaisons équipotentielles",
                groundingDescription = "Réseau de terre &lt; 10 Ω et liaisons équipotentielles entre toutes les masses métalliques — prérequis à toute protection efficace.",
                surgeTitle = "Parafoudres type 1 + type 2 sur les tableaux électriques",
                surgeDescription = "Avec %s impacts en zone 1–2 km, les surtensions induites sont une menace réelle pour les équipements sensibles.",
                riskAssessmentTitle = "Audit de conformité IEC 62305-2:2024",
                riskAssessmentDescription = "Un rapport d'évaluation du risque foudre (ERF) est la pièce maîtresse pour les assureurs et la conformité réglementaire.",
                alertsTitle = "Alertes foudre temps réel — Sky Sentinel",
                alertsDescription = "Dès qu'un orage approche à moins de 50 km, vos équipes reçoivent une alerte avant l'impact. Strike Radar analyse le passé ; Sky Sentinel protège le présent.",
            ),
            methodology = MethodologyText(
                header = "Annexe — Méthodologie &amp; sources",
                section = "Méthodologie &amp; sources",
                networkLabel = "Réseau de détection :",
                networkText = "GLD360+ — réseau mondial longue portée, efficacité de détection &gt;95%, précision de localisation &lt;&nbsp;500&nbsp;m. Chaque impact est identifié par un ID unique et horodaté en UTC.",
                referencesLabel = "Normes appliquées :",
                iec62305 = "Évaluation du risque foudre : gestion du risque, définition des zones de danger, critères d'analyse par distance.",
                iec62858 = "Densité de foudroiement Nsg par réseau de détection : méthode de mesure, fiabilité selon nombre de capteurs contributeurs.",
                nfc17102 = "Paratonnerres à dispositif d'amorçage (PDA) : applicable aux certificats de foudroiement.",
                coverageLabel = "Couverture géographique :",
                coverageText = "Tous les impacts nuage-sol (CG±) dans un rayon de 2 km autour du site, sur la période complète sélectionnée.",
                polarityLabel = "Polarité :",
                negativePolarity = "Bleu = foudre négative (CG−) — représente 95–97 % des impacts mondiaux.",
                positivePolarity = "Rouge = foudre positive (CG+) — 3–5 % des impacts mais concentre les intensités les plus élevées (jusqu'à 10× supérieures).",
                unitsLabel = "Unités &amp; calculs :",
                unitsText = "Intensités exprimées en kA (kiloampères). La médiane est préférable à la moyenne en présence de valeurs extrêmes (CG+). Un nombre de capteurs contributeurs ≥ 5 indique une localisation fiable (IEC 62858:2019).",
                zonesLabel = "Zones d'analyse :",
                zonesText = "0–1 km = danger direct (risque vital, incendie, destruction). 1–2 km = danger indirect (surtensions LEMP, automates, SCADA). Au-delà de 2 km : contexte général du régime orageux.",
                observations = "Observations",
            ),
            highlights = HighlightsText(
                title = "Faits marquants",
                strongestImpact = "Impact le plus intense",
                nearestImpact = "Impact le plus proche",
                dateUnavailable = "date non disponible",
                noGroundImpact = "Aucun impact au sol (nuage-sol) à moins de 2 km sur la période.",
                at = "à",
            ),
        )

        private val en = KeraunicReportText(
            lang = PdfOutputLang.EN,
            reportTitle = "Keraunic Report",
            generatedOn = "Generated on",
            editedOn = "Issued on",
            westCardinal = "W",
            shortMonths = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"),
            longMonths = listOf("January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"),
            cover = CoverText(
                subtitle = "Comprehensive analysis of lightning activity",
                period = "Period",
                impactsDetected = "Detected strikes (0–2 km)",
                nsg = "Nsg (strikes/km²/year)",
                maximumIntensity = "Maximum intensity",
                directDanger = "Direct danger (&lt; 1 km)",
                nsgLine = "Nsg = %s strikes/km²/year",
                highActivity = "High activity",
                moderateActivity = "Moderate activity",
                lowActivity = "Low activity",
                referencePrefix = "— Ref. IEC 62858:2019",
            ),
            overview = OverviewText(
                header = "Overview — Notable strikes",
                notableImpacts = "Notable strikes",
                topSubtitle = "Notable strikes near the site (0–2 km zone)",
                fallbackSubtitle = "Closest, strongest and weakest strikes near the site (0–2 km zone)",
                missingTopImpacts = "Top-impact data unavailable",
                analysis = "Keraunic analysis of the site",
                nsgComparison = "Comparison with nearby cities (Nsg)",
                yourSite = "Your site",
                nearestCities = "Comparison with nearby cities (Nsg)",
                detectionNetwork = "GLD360+ detection network",
                detectionDescription = "A global, long-range network with a detection efficiency of &gt;95% and location accuracy of &lt;&nbsp;500&nbsp;m. Each strike is assigned a unique ID and timestamped in UTC.",
                nearestImpact = "Closest",
                strongestImpact = "Strongest",
                weakestImpact = "Weakest",
            ),
            map = MapText(
                header = "Strike map",
                title = "Geographic distribution of strikes",
                description = "Each dot represents a strike detected by the GLD360+ network during the study period.",
                positive = "positive",
                negative = "negative",
                directCount = "strikes &lt;1 km",
                directDanger = "Direct danger zone",
                indirectCount = "strikes 1–2 km",
                indirectDanger = "Indirect danger zone",
            ),
            zone = ZoneText(
                directDanger = "DIRECT DANGER",
                indirectDanger = "INDIRECT DANGER",
                directSubtitle = "Direct danger zone — 0 to 1 km",
                indirectSubtitle = "Indirect danger zone — 1 to 2 km",
                directDescription = "In this area, a lightning strike can directly hit structures, equipment and people.",
                indirectDescription = "In this area, lightning does not strike the site but produces measurable effects at a distance.",
                zoneDirect = "Direct danger",
                zoneIndirect = "Indirect danger",
                recordedImpacts = "strikes recorded",
                noImpactsInZone = "No strikes recorded in this zone over the period.",
                polarityDistribution = "Polarity distribution",
                negativeLightning = "Negative lightning (CG−)",
                positiveLightning = "Positive lightning (CG+)",
                impactCount = "Strike count",
                maximumIntensity = "Maximum intensity",
                averageIntensity = "Average intensity",
                median = "Median",
                keyPoints = "Key points — Direct danger zone",
                mostIntenseImpact = "Highest-intensity strike",
                strongestPositiveImpact = "Strongest positive CG+ strike",
                distanceFromSite = "Distance from site: %s km",
                moreDestructive = "2 to 3 times more destructive than negative lightning",
            ),
            effects = EffectsText(
                header = "Potential effects &amp; broader context",
                section = "Potential effects &amp; broader context",
                directTitle = "Direct strikes — 0–1 km zone (immediate danger)",
                fire = "Fire",
                fireDescription = "A direct strike on a structure may start a fire through an electrical arc or the ignition of combustible materials.",
                structuralDamage = "Destruction / structural damage",
                structuralDamageDescription = "High lightning currents (&gt; 50 kA) generate mechanical and thermal forces that cause lasting structural damage.",
                lifeSafety = "Risk to life",
                lifeSafetyDescription = "Lethal step and touch voltages occur within a 15–20 m radius of the strike point.",
                indirectTitle = "Indirect strikes — LEMP effects (0–2 km)",
                lempSurges = "LEMP surges",
                lempDescription = "The lightning electromagnetic pulse (LEMP) induces transient overvoltages on all conductors: power cables, data links and electrical panels.",
                automation = "Automation &amp; SCADA",
                automationDescription = "Industrial control systems and SCADA are particularly vulnerable to electromagnetic disturbances.",
                synthesis = "Summary",
                synthesisText = "With a maximum intensity of %s kA recorded within 2 km, %s direct strikes (0–1 km) and %s indirect strikes (1–2 km) during the period, the overall risk level is: %s.",
                synthesisRiskNote = "The risks of fire, structural damage and destruction of sensitive equipment are established and cannot be considered negligible.",
                synthesisLowNote = "Indirect effects (LEMP surges) may nevertheless affect sensitive equipment.",
            ),
            charts = ChartsText(
                monthlyActivity = "Monthly activity",
                monthlyAndIntensityDistribution = "Monthly activity &amp; intensity distribution",
                zone = "Zone",
                strikesPerMonth = "strikes/month",
                monthlyIntensityHeader = "Monthly CG−/CG+ intensity",
                averageMonthlyIntensity = "Average monthly intensity by zone (kA)",
                monthlyIntensityDescription = "Average intensity of CG− and CG+ strikes by month. Intensity peaks reveal the most dangerous months.",
                averageKaPerMonth = "average kA/month",
                hourlyDistributionHeader = "Hourly activity distribution",
                hourlyDistribution = "Hourly distribution (UTC) — CG−/CG+ polarity",
                allImpactsAroundSite = "All strikes around the site",
                combinedZones = "combined 0–1 km and 1–2 km zones",
                monthlyHourlyIntensityHeader = "Hourly intensity — CG−/CG+",
                averageHourlyIntensity = "Average intensity by UTC hour (kA)",
                dailyProfile = "The diurnal profile reveals an intensification that adds to the peak in strike frequency.",
                averageKaPerHour = "average kA/hour UTC, combined zones",
                negativeSeries = "CG− (negative)",
                positiveSeries = "CG+ (positive)",
                notAvailable = "Data unavailable",
            ),
            recommendations = RecommendationsText(
                header = "Personalized assessment &amp; recommendations",
                riskHigh = "HIGH RISK",
                riskModerate = "MODERATE RISK",
                riskLow = "LOW RISK",
                gaugeLow = "LOW",
                gaugeCritical = "CRITICAL",
                scoreDescription = "Score calculated on the basis of %s direct strikes (0–1 km), the highest observed intensity (%s kA) and Nsg (%s).",
                actionPlan = "Recommended action plan",
                priority1 = "PRIORITY 1",
                priority2 = "PRIORITY 2",
                advice = "ADVICE",
                skySentinel = "SKY SENTINEL",
                installRodTitle = "Installation of a PDA lightning conductor (NF C 17-102:2011)",
                installRodDescription = "With %s direct strikes during the period and a maximum intensity of %s kA, installing an early streamer emission lightning conductor is essential.",
                groundingTitle = "Grounding and equipotential bonding",
                groundingDescription = "Grounding network &lt; 10 Ω and equipotential bonding between all metal parts — prerequisites for effective protection.",
                surgeTitle = "Type 1 + type 2 surge protectors for electrical panels",
                surgeDescription = "With %s strikes in the 1–2 km zone, induced surges are a real threat to sensitive equipment.",
                riskAssessmentTitle = "IEC 62305-2:2024 compliance audit",
                riskAssessmentDescription = "A lightning risk assessment report is the cornerstone for insurers and regulatory compliance.",
                alertsTitle = "Real-time lightning alerts — Sky Sentinel",
                alertsDescription = "As soon as a storm approaches within 50 km, your teams receive an alert before the strike. Strike Radar analyzes the past; Sky Sentinel protects the present.",
            ),
            methodology = MethodologyText(
                header = "Appendix — Methodology &amp; sources",
                section = "Methodology &amp; sources",
                networkLabel = "Detection network:",
                networkText = "GLD360+ — a global, long-range network with a detection efficiency of &gt;95% and location accuracy of &lt;&nbsp;500&nbsp;m. Each strike is assigned a unique ID and timestamped in UTC.",
                referencesLabel = "Standards applied:",
                iec62305 = "Lightning risk assessment: risk management, definition of danger zones, and distance-based analysis criteria.",
                iec62858 = "Nsg ground flash density by detection network: measurement method, reliability based on the number of contributing sensors.",
                nfc17102 = "Early streamer emission (ESE) lightning rods: applicable to lightning-strike certificates.",
                coverageLabel = "Geographic coverage:",
                coverageText = "All cloud-to-ground strikes (CG±) within a 2 km radius of the site over the full selected period.",
                polarityLabel = "Polarity:",
                negativePolarity = "Blue = negative lightning (CG−) — represents 95–97% of strikes worldwide.",
                positivePolarity = "Red = positive lightning (CG+) — 3–5% of strikes but concentrates the highest intensities (up to 10× greater).",
                unitsLabel = "Units &amp; calculations:",
                unitsText = "Intensity is expressed in kA (kiloamperes). The median is preferable to the mean when extreme values are present, particularly for CG+. A count of ≥ 5 contributing sensors indicates reliable location (IEC 62858:2019).",
                zonesLabel = "Analysis zones:",
                zonesText = "0–1 km = direct danger (risk of fatal injury, fire, destruction). 1–2 km = indirect danger (LEMP surges, automation, SCADA). Beyond 2 km: general thunderstorm-regime context.",
                observations = "Observations",
            ),
            highlights = HighlightsText(
                title = "Key facts",
                strongestImpact = "Most intense strike",
                nearestImpact = "Nearest strike",
                dateUnavailable = "date not available",
                noGroundImpact = "No cloud-to-ground strike within 2 km over the period.",
                at = "at",
            ),
        )

        private val es = KeraunicReportText(
            lang = PdfOutputLang.ES,
            reportTitle = "Informe ceráunico",
            generatedOn = "Generado el",
            editedOn = "Emitido el",
            westCardinal = "O",
            shortMonths = listOf("Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"),
            longMonths = listOf("enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"),
            cover = CoverText(
                subtitle = "Análisis completo de la actividad de rayos",
                period = "Período",
                impactsDetected = "Impactos detectados (0–2 km)",
                nsg = "Densidad Nsg (impactos/km²/año)",
                maximumIntensity = "Intensidad máxima",
                directDanger = "Peligro directo (&lt; 1 km)",
                nsgLine = "Nsg = %s impactos/km²/año",
                highActivity = "Actividad elevada",
                moderateActivity = "Actividad moderada",
                lowActivity = "Actividad baja",
                referencePrefix = "— Ref. IEC 62858:2019",
            ),
            overview = OverviewText(
                header = "Resumen — Impactos destacados",
                notableImpacts = "Impactos destacados",
                topSubtitle = "Impactos destacados cerca del sitio (zona de 0–2 km)",
                fallbackSubtitle = "Impacto más cercano, más intenso y menos intenso del sitio (zona de 0–2 km)",
                missingTopImpacts = "Datos de impactos destacados no disponibles",
                analysis = "Análisis ceráunico del sitio",
                nsgComparison = "Comparación con ciudades cercanas (Nsg)",
                yourSite = "Su sitio",
                nearestCities = "Comparación con ciudades cercanas (Nsg)",
                detectionNetwork = "Red de detección GLD360+",
                detectionDescription = "Red mundial de largo alcance, con una eficacia de detección &gt;95 % y una precisión de localización &lt;&nbsp;500&nbsp;m. Cada impacto recibe un ID único y una marca de tiempo UTC.",
                nearestImpact = "Más cercano",
                strongestImpact = "Más intenso",
                weakestImpact = "Menos intenso",
            ),
            map = MapText(
                header = "Mapa de impactos",
                title = "Distribución geográfica de los impactos",
                description = "Cada punto representa un impacto detectado por la red GLD360+ durante el período de estudio.",
                positive = "positivo",
                negative = "negativo",
                directCount = "impactos &lt;1 km",
                directDanger = "Zona de peligro directo",
                indirectCount = "impactos 1–2 km",
                indirectDanger = "Zona de peligro indirecto",
            ),
            zone = ZoneText(
                directDanger = "PELIGRO DIRECTO",
                indirectDanger = "PELIGRO INDIRECTO",
                directSubtitle = "Zona de peligro directo — de 0 a 1 km",
                indirectSubtitle = "Zona de peligro indirecto — de 1 a 2 km",
                directDescription = "En esta zona, un rayo puede alcanzar directamente las estructuras, los equipos y las personas.",
                indirectDescription = "En esta zona, el rayo no impacta en el sitio, pero genera efectos medibles a distancia.",
                zoneDirect = "Peligro directo",
                zoneIndirect = "Peligro indirecto",
                recordedImpacts = "impactos registrados",
                noImpactsInZone = "Ningún impacto registrado en esta zona durante el período.",
                polarityDistribution = "Distribución por polaridad",
                negativeLightning = "Rayo negativo (CG−)",
                positiveLightning = "Rayo positivo (CG+)",
                impactCount = "Número de impactos",
                maximumIntensity = "Intensidad máxima",
                averageIntensity = "Intensidad media",
                median = "Mediana",
                keyPoints = "Puntos clave — Zona de peligro directo",
                mostIntenseImpact = "Impacto de mayor intensidad",
                strongestPositiveImpact = "Impacto CG+ más intenso",
                distanceFromSite = "Distancia al sitio: %s km",
                moreDestructive = "2 a 3 veces más destructivo que el rayo negativo",
            ),
            effects = EffectsText(
                header = "Efectos posibles &amp; contexto ampliado",
                section = "Efectos posibles &amp; contexto ampliado",
                directTitle = "Impactos directos — zona de 0–1 km (peligro inmediato)",
                fire = "Incendio",
                fireDescription = "Un impacto directo sobre una estructura puede iniciar un incendio por arco eléctrico o por la ignición de materiales combustibles.",
                structuralDamage = "Destrucción / daños estructurales",
                structuralDamageDescription = "Las corrientes elevadas de rayo (&gt; 50 kA) generan fuerzas mecánicas y térmicas que dañan duraderamente las estructuras.",
                lifeSafety = "Riesgo para la vida",
                lifeSafetyDescription = "Tensiones de paso y de contacto letales en un radio de 15–20 m alrededor del punto de impacto.",
                indirectTitle = "Impactos indirectos — efectos LEMP (0–2 km)",
                lempSurges = "Sobretensiones LEMP",
                lempDescription = "El impulso electromagnético del rayo (LEMP) induce sobretensiones transitorias en todos los conductores: cables de alimentación, enlaces de datos y cuadros eléctricos.",
                automation = "Autómatas y SCADA",
                automationDescription = "Los sistemas de control industrial y SCADA son especialmente vulnerables a las perturbaciones electromagnéticas.",
                synthesis = "Síntesis",
                synthesisText = "Con una intensidad máxima de %s kA registrada a menos de 2 km, %s impactos directos (0–1 km) y %s impactos indirectos (1–2 km) durante el período, el nivel de riesgo global es: %s.",
                synthesisRiskNote = "Los riesgos de incendio, daño estructural y destrucción de equipos sensibles están comprobados y no son insignificantes.",
                synthesisLowNote = "Los efectos indirectos (sobretensiones LEMP) pueden, no obstante, afectar a equipos sensibles.",
            ),
            charts = ChartsText(
                monthlyActivity = "Actividad mensual",
                monthlyAndIntensityDistribution = "Actividad mensual y distribución de intensidad",
                zone = "Zona",
                strikesPerMonth = "impactos/mes",
                monthlyIntensityHeader = "Intensidad mensual CG−/CG+",
                averageMonthlyIntensity = "Intensidad media mensual por zona (kA)",
                monthlyIntensityDescription = "Intensidad media de los impactos CG− y CG+ por mes. Los picos de intensidad revelan los meses más peligrosos.",
                averageKaPerMonth = "kA medio/mes",
                hourlyDistributionHeader = "Distribución horaria de la actividad",
                hourlyDistribution = "Distribución horaria (UTC) — polaridad CG−/CG+",
                allImpactsAroundSite = "Todos los impactos alrededor del sitio",
                combinedZones = "zonas de 0–1 km y 1–2 km combinadas",
                monthlyHourlyIntensityHeader = "Intensidad horaria — CG−/CG+",
                averageHourlyIntensity = "Intensidad media por hora UTC (kA)",
                dailyProfile = "El perfil diurno revela una intensificación que se suma al pico de frecuencia de impactos.",
                averageKaPerHour = "kA medio/hora UTC, zonas combinadas",
                negativeSeries = "CG− (negativos)",
                positiveSeries = "CG+ (positivos)",
                notAvailable = "Datos no disponibles",
            ),
            recommendations = RecommendationsText(
                header = "Diagnóstico personalizado y recomendaciones",
                riskHigh = "RIESGO ELEVADO",
                riskModerate = "RIESGO MODERADO",
                riskLow = "RIESGO BAJO",
                gaugeLow = "BAJO",
                gaugeCritical = "CRÍTICO",
                scoreDescription = "Puntuación calculada a partir de %s impactos directos (0–1 km), la intensidad máxima observada (%s kA) y el Nsg (%s).",
                actionPlan = "Plan de acción recomendado",
                priority1 = "PRIORIDAD 1",
                priority2 = "PRIORIDAD 2",
                advice = "RECOMENDACIÓN",
                skySentinel = "SKY SENTINEL",
                installRodTitle = "Instalación de un pararrayos PDA (NF C 17-102:2011)",
                installRodDescription = "A la vista de los %s impactos directos registrados durante el período y de una intensidad máxima de %s kA, es imprescindible instalar un pararrayos con dispositivo de cebado.",
                groundingTitle = "Puesta a tierra y conexiones equipotenciales",
                groundingDescription = "Una red de puesta a tierra &lt; 10 Ω y las conexiones equipotenciales entre todas las masas metálicas son requisitos previos para una protección eficaz.",
                surgeTitle = "Protectores contra sobretensiones tipo 1 + tipo 2 en los cuadros eléctricos",
                surgeDescription = "Con %s impactos en la zona de 1–2 km, las sobretensiones inducidas representan una amenaza real para los equipos sensibles.",
                riskAssessmentTitle = "Auditoría de conformidad IEC 62305-2:2024",
                riskAssessmentDescription = "Un informe de evaluación del riesgo de rayo (ERF) es la pieza clave para las aseguradoras y el cumplimiento reglamentario.",
                alertsTitle = "Alertas de rayos en tiempo real — Sky Sentinel",
                alertsDescription = "En cuanto una tormenta se acerca a menos de 50 km, sus equipos reciben una alerta antes del impacto. Strike Radar analiza el pasado; Sky Sentinel protege el presente.",
            ),
            methodology = MethodologyText(
                header = "Anexo — Metodología y fuentes",
                section = "Metodología y fuentes",
                networkLabel = "Red de detección:",
                networkText = "GLD360+ — red mundial de largo alcance, con una eficacia de detección &gt;95 % y una precisión de localización &lt;&nbsp;500&nbsp;m. Cada impacto recibe un ID único y una marca de tiempo UTC.",
                referencesLabel = "Normas aplicadas:",
                iec62305 = "Evaluación del riesgo de rayo: gestión del riesgo, definición de las zonas de peligro y criterios de análisis por distancia.",
                iec62858 = "Densidad de descargas a tierra Nsg por red de detección: método de medición, fiabilidad según el número de sensores contribuyentes.",
                nfc17102 = "Pararrayos con dispositivo de cebado (PDA): aplicable a los certificados de impacto de rayo.",
                coverageLabel = "Cobertura geográfica:",
                coverageText = "Todos los impactos nube-tierra (CG±) en un radio de 2 km alrededor del sitio, durante todo el período seleccionado.",
                polarityLabel = "Polaridad:",
                negativePolarity = "Azul = rayo negativo (CG−) — representa el 95–97 % de los impactos mundiales.",
                positivePolarity = "Rojo = rayo positivo (CG+) — 3–5 % de los impactos, pero concentra las intensidades más elevadas (hasta 10 veces superiores).",
                unitsLabel = "Unidades y cálculos:",
                unitsText = "Las intensidades se expresan en kA (kiloamperios). La mediana es preferible a la media cuando hay valores extremos (CG+). Un número de sensores contribuyentes ≥ 5 indica una localización fiable (IEC 62858:2019).",
                zonesLabel = "Zonas de análisis:",
                zonesText = "0–1 km = peligro directo (riesgo de muerte, incendio y destrucción). 1–2 km = peligro indirecto (sobretensiones LEMP, autómatas, SCADA). Más allá de 2 km: contexto general del régimen de tormentas.",
                observations = "Observaciones",
            ),
            highlights = HighlightsText(
                title = "Hechos destacados",
                strongestImpact = "Impacto más intenso",
                nearestImpact = "Impacto más cercano",
                dateUnavailable = "fecha no disponible",
                noGroundImpact = "Ningún impacto nube-tierra a menos de 2 km durante el período.",
                at = "a las",
            ),
        )

        private val pt = KeraunicReportText(
            lang = PdfOutputLang.PT,
            reportTitle = "Relatório ceráunico",
            generatedOn = "Gerado em",
            editedOn = "Emitido em",
            westCardinal = "O",
            shortMonths = listOf("Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"),
            longMonths = listOf("janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"),
            cover = CoverText(
                subtitle = "Análise completa da atividade de descargas atmosféricas",
                period = "Período",
                impactsDetected = "Impactos detetados (0–2 km)",
                nsg = "Nsg (impactos/km²/ano)",
                maximumIntensity = "Intensidade máxima",
                directDanger = "Perigo direto (&lt; 1 km)",
                nsgLine = "Nsg = %s impactos/km²/ano",
                highActivity = "Atividade elevada",
                moderateActivity = "Atividade moderada",
                lowActivity = "Atividade baixa",
                referencePrefix = "— Ref. IEC 62858:2019",
            ),
            overview = OverviewText(
                header = "Visão geral — Impactos em destaque",
                notableImpacts = "Impactos em destaque",
                topSubtitle = "Impactos em destaque perto do local (zona de 0–2 km)",
                fallbackSubtitle = "Impacto mais próximo, mais intenso e menos intenso do local (zona de 0–2 km)",
                missingTopImpacts = "Dados dos impactos em destaque indisponíveis",
                analysis = "Análise ceráunica do local",
                nsgComparison = "Comparação com cidades próximas (Nsg)",
                yourSite = "O seu local",
                nearestCities = "Comparação com cidades próximas (Nsg)",
                detectionNetwork = "Rede de deteção GLD360+",
                detectionDescription = "Rede mundial de longo alcance, com eficácia de deteção &gt;95 % e precisão de localização &lt;&nbsp;500&nbsp;m. Cada impacto recebe um ID único e uma marca temporal UTC.",
                nearestImpact = "Mais próximo",
                strongestImpact = "Mais intenso",
                weakestImpact = "Menos intenso",
            ),
            map = MapText(
                header = "Mapa de impactos",
                title = "Distribuição geográfica dos impactos",
                description = "Cada ponto representa um impacto detetado pela rede GLD360+ durante o período de estudo.",
                positive = "positivo",
                negative = "negativo",
                directCount = "impactos &lt;1 km",
                directDanger = "Zona de perigo direto",
                indirectCount = "impactos 1–2 km",
                indirectDanger = "Zona de perigo indireto",
            ),
            zone = ZoneText(
                directDanger = "PERIGO DIRETO",
                indirectDanger = "PERIGO INDIRETO",
                directSubtitle = "Zona de perigo direto — 0 a 1 km",
                indirectSubtitle = "Zona de perigo indireto — 1 a 2 km",
                directDescription = "Nesta zona, um raio pode atingir diretamente as estruturas, os equipamentos e as pessoas.",
                indirectDescription = "Nesta zona, a descarga atmosférica não atinge o local, mas gera efeitos mensuráveis à distância.",
                zoneDirect = "Perigo direto",
                zoneIndirect = "Perigo indireto",
                recordedImpacts = "impactos registados",
                noImpactsInZone = "Nenhum impacto registado nesta zona durante o período.",
                polarityDistribution = "Distribuição por polaridade",
                negativeLightning = "Descarga negativa (CG−)",
                positiveLightning = "Descarga positiva (CG+)",
                impactCount = "Número de impactos",
                maximumIntensity = "Intensidade máxima",
                averageIntensity = "Intensidade média",
                median = "Mediana",
                keyPoints = "Pontos principais — Zona de perigo direto",
                mostIntenseImpact = "Impacto de maior intensidade",
                strongestPositiveImpact = "Impacto CG+ mais intenso",
                distanceFromSite = "Distância ao local: %s km",
                moreDestructive = "2 a 3 vezes mais destrutiva do que a descarga negativa",
            ),
            effects = EffectsText(
                header = "Possíveis efeitos &amp; contexto alargado",
                section = "Possíveis efeitos &amp; contexto alargado",
                directTitle = "Impactos diretos — zona de 0–1 km (perigo imediato)",
                fire = "Incêndio",
                fireDescription = "Um impacto direto numa estrutura pode iniciar um incêndio por arco elétrico ou pela ignição de materiais combustíveis.",
                structuralDamage = "Destruição / danos estruturais",
                structuralDamageDescription = "Correntes elevadas de descargas atmosféricas (&gt; 50 kA) geram forças mecânicas e térmicas que danificam duradouramente as estruturas.",
                lifeSafety = "Risco para a vida",
                lifeSafetyDescription = "Tensões de passo e de contacto letais num raio de 15–20 m em redor do ponto de impacto.",
                indirectTitle = "Impactos indiretos — efeitos LEMP (0–2 km)",
                lempSurges = "Sobretensões LEMP",
                lempDescription = "O impulso eletromagnético da descarga (LEMP) induz sobretensões transitórias em todos os condutores: cabos de alimentação, ligações de dados e quadros elétricos.",
                automation = "Autómatos &amp; SCADA",
                automationDescription = "Os sistemas de controlo industrial e SCADA são particularmente vulneráveis a perturbações eletromagnéticas.",
                synthesis = "Síntese",
                synthesisText = "Com uma intensidade máxima de %s kA registada a menos de 2 km, %s impactos diretos (0–1 km) e %s impactos indiretos (1–2 km) durante o período, o nível de risco global é: %s.",
                synthesisRiskNote = "Os riscos de incêndio, dano estrutural e destruição de equipamentos sensíveis estão comprovados e não são negligenciáveis.",
                synthesisLowNote = "Os efeitos indiretos (sobretensões LEMP) podem, ainda assim, afetar equipamentos sensíveis.",
            ),
            charts = ChartsText(
                monthlyActivity = "Atividade mensal",
                monthlyAndIntensityDistribution = "Atividade mensal e distribuição da intensidade",
                zone = "Zona",
                strikesPerMonth = "impactos/mês",
                monthlyIntensityHeader = "Intensidade mensal CG−/CG+",
                averageMonthlyIntensity = "Intensidade média mensal por zona (kA)",
                monthlyIntensityDescription = "Intensidade média dos impactos CG− e CG+ por mês. Os picos de intensidade revelam os meses mais perigosos.",
                averageKaPerMonth = "kA médio/mês",
                hourlyDistributionHeader = "Distribuição horária da atividade",
                hourlyDistribution = "Distribuição horária (UTC) — polaridade CG−/CG+",
                allImpactsAroundSite = "Todos os impactos em redor do local",
                combinedZones = "zonas de 0–1 km e 1–2 km combinadas",
                monthlyHourlyIntensityHeader = "Intensidade horária — CG−/CG+",
                averageHourlyIntensity = "Intensidade média por hora UTC (kA)",
                dailyProfile = "O perfil diurno revela uma intensificação que se soma ao pico da frequência de impactos.",
                averageKaPerHour = "kA médio/hora UTC, zonas combinadas",
                negativeSeries = "CG− (negativos)",
                positiveSeries = "CG+ (positivos)",
                notAvailable = "Dados indisponíveis",
            ),
            recommendations = RecommendationsText(
                header = "Diagnóstico personalizado &amp; recomendações",
                riskHigh = "RISCO ELEVADO",
                riskModerate = "RISCO MODERADO",
                riskLow = "RISCO BAIXO",
                gaugeLow = "BAIXO",
                gaugeCritical = "CRÍTICO",
                scoreDescription = "Pontuação calculada com base em %s impactos diretos (0–1 km), na intensidade máxima observada (%s kA) e no Nsg (%s).",
                actionPlan = "Plano de ação recomendado",
                priority1 = "PRIORIDADE 1",
                priority2 = "PRIORIDADE 2",
                advice = "RECOMENDAÇÃO",
                skySentinel = "SKY SENTINEL",
                installRodTitle = "Instalação de um para-raios PDA (NF C 17-102:2011)",
                installRodDescription = "Tendo em conta os %s impactos diretos registados durante o período e uma intensidade máxima de %s kA, é indispensável instalar um para-raios com dispositivo de ionização.",
                groundingTitle = "Ligação à terra e ligações equipotenciais",
                groundingDescription = "Uma rede de terra &lt; 10 Ω e ligações equipotenciais entre todas as massas metálicas são pré-requisitos para qualquer proteção eficaz.",
                surgeTitle = "Descarregadores de sobretensão tipo 1 + tipo 2 nos quadros elétricos",
                surgeDescription = "Com %s impactos na zona de 1–2 km, as sobretensões induzidas representam uma ameaça real para os equipamentos sensíveis.",
                riskAssessmentTitle = "Auditoria de conformidade IEC 62305-2:2024",
                riskAssessmentDescription = "Um relatório de avaliação do risco de descargas atmosféricas (ERF) é a peça-chave para as seguradoras e para a conformidade regulamentar.",
                alertsTitle = "Alertas de descargas atmosféricas em tempo real — Sky Sentinel",
                alertsDescription = "Assim que uma tempestade se aproxima a menos de 50 km, as suas equipas recebem um alerta antes do impacto. O Strike Radar analisa o passado; o Sky Sentinel protege o presente.",
            ),
            methodology = MethodologyText(
                header = "Anexo — Metodologia &amp; fontes",
                section = "Metodologia &amp; fontes",
                networkLabel = "Rede de deteção:",
                networkText = "GLD360+ — rede mundial de longo alcance, com eficácia de deteção &gt;95 % e precisão de localização &lt;&nbsp;500&nbsp;m. Cada impacto recebe um ID único e uma marca temporal UTC.",
                referencesLabel = "Normas aplicadas:",
                iec62305 = "Avaliação do risco de descargas atmosféricas: gestão do risco, definição das zonas de perigo e critérios de análise por distância.",
                iec62858 = "Densidade de descargas à terra Nsg por rede de deteção: método de medição e fiabilidade segundo o número de sensores contribuintes.",
                nfc17102 = "Para-raios com dispositivo de iniciação (PDA): aplicável a certificados de descargas atmosféricas.",
                coverageLabel = "Cobertura geográfica:",
                coverageText = "Todos os impactos nuvem-solo (CG±) num raio de 2 km em redor do local, durante todo o período selecionado.",
                polarityLabel = "Polaridade:",
                negativePolarity = "Azul = descarga negativa (CG−) — representa 95–97 % dos impactos mundiais.",
                positivePolarity = "Vermelho = descarga positiva (CG+) — 3–5 % dos impactos, mas concentra as intensidades mais elevadas (até 10× superiores).",
                unitsLabel = "Unidades &amp; cálculos:",
                unitsText = "As intensidades são expressas em kA (quiloampere). A mediana é preferível à média quando existem valores extremos (CG+). Um número de sensores contribuintes ≥ 5 indica uma localização fiável (IEC 62858:2019).",
                zonesLabel = "Zonas de análise:",
                zonesText = "0–1 km = perigo direto (risco de morte, incêndio, destruição). 1–2 km = perigo indireto (sobretensões LEMP, autómatos, SCADA). Para além de 2 km: contexto geral do regime de tempestades.",
                observations = "Observações",
            ),
            highlights = HighlightsText(
                title = "Factos marcantes",
                strongestImpact = "Impacto mais intenso",
                nearestImpact = "Impacto mais próximo",
                dateUnavailable = "data não disponível",
                noGroundImpact = "Nenhum impacto nuvem-solo a menos de 2 km durante o período.",
                at = "às",
            ),
        )
    }
}

internal data class CoverText(
    val subtitle: String,
    val period: String,
    val impactsDetected: String,
    val nsg: String,
    val maximumIntensity: String,
    val directDanger: String,
    val nsgLine: String,
    val highActivity: String,
    val moderateActivity: String,
    val lowActivity: String,
    val referencePrefix: String,
)

internal data class OverviewText(
    val header: String,
    val notableImpacts: String,
    val topSubtitle: String,
    val fallbackSubtitle: String,
    val missingTopImpacts: String,
    val analysis: String,
    val nsgComparison: String,
    val yourSite: String,
    val nearestCities: String,
    val detectionNetwork: String,
    val detectionDescription: String,
    val nearestImpact: String,
    val strongestImpact: String,
    val weakestImpact: String,
)

internal data class MapText(
    val header: String,
    val title: String,
    val description: String,
    val positive: String,
    val negative: String,
    val directCount: String,
    val directDanger: String,
    val indirectCount: String,
    val indirectDanger: String,
)

internal data class ZoneText(
    val directDanger: String,
    val indirectDanger: String,
    val directSubtitle: String,
    val indirectSubtitle: String,
    val directDescription: String,
    val indirectDescription: String,
    val zoneDirect: String,
    val zoneIndirect: String,
    val recordedImpacts: String,
    val noImpactsInZone: String,
    val polarityDistribution: String,
    val negativeLightning: String,
    val positiveLightning: String,
    val impactCount: String,
    val maximumIntensity: String,
    val averageIntensity: String,
    val median: String,
    val keyPoints: String,
    val mostIntenseImpact: String,
    val strongestPositiveImpact: String,
    val distanceFromSite: String,
    val moreDestructive: String,
)

internal data class EffectsText(
    val header: String,
    val section: String,
    val directTitle: String,
    val fire: String,
    val fireDescription: String,
    val structuralDamage: String,
    val structuralDamageDescription: String,
    val lifeSafety: String,
    val lifeSafetyDescription: String,
    val indirectTitle: String,
    val lempSurges: String,
    val lempDescription: String,
    val automation: String,
    val automationDescription: String,
    val synthesis: String,
    val synthesisText: String,
    val synthesisRiskNote: String,
    val synthesisLowNote: String,
)

internal data class ChartsText(
    val monthlyActivity: String,
    val monthlyAndIntensityDistribution: String,
    val zone: String,
    val strikesPerMonth: String,
    val monthlyIntensityHeader: String,
    val averageMonthlyIntensity: String,
    val monthlyIntensityDescription: String,
    val averageKaPerMonth: String,
    val hourlyDistributionHeader: String,
    val hourlyDistribution: String,
    val allImpactsAroundSite: String,
    val combinedZones: String,
    val monthlyHourlyIntensityHeader: String,
    val averageHourlyIntensity: String,
    val dailyProfile: String,
    val averageKaPerHour: String,
    val negativeSeries: String,
    val positiveSeries: String,
    val notAvailable: String,
)

internal data class RecommendationsText(
    val header: String,
    val riskHigh: String,
    val riskModerate: String,
    val riskLow: String,
    val gaugeLow: String,
    val gaugeCritical: String,
    val scoreDescription: String,
    val actionPlan: String,
    val priority1: String,
    val priority2: String,
    val advice: String,
    val skySentinel: String,
    val installRodTitle: String,
    val installRodDescription: String,
    val groundingTitle: String,
    val groundingDescription: String,
    val surgeTitle: String,
    val surgeDescription: String,
    val riskAssessmentTitle: String,
    val riskAssessmentDescription: String,
    val alertsTitle: String,
    val alertsDescription: String,
)

internal data class MethodologyText(
    val header: String,
    val section: String,
    val networkLabel: String,
    val networkText: String,
    val referencesLabel: String,
    val iec62305: String,
    val iec62858: String,
    val nfc17102: String,
    val coverageLabel: String,
    val coverageText: String,
    val polarityLabel: String,
    val negativePolarity: String,
    val positivePolarity: String,
    val unitsLabel: String,
    val unitsText: String,
    val zonesLabel: String,
    val zonesText: String,
    val observations: String,
)

internal data class HighlightsText(
    val title: String,
    val strongestImpact: String,
    val nearestImpact: String,
    val dateUnavailable: String,
    val noGroundImpact: String,
    // Word between the date and the hour
    val at: String,
)
